import { satepsiApi, SatepsiPaginatedResponse, SatepsiTest } from '@/api/satepsi';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const PAGE_SIZE = 50;

interface UseSatepsiTestsParams {
    query: string;
    construct?: string;
}

export function useSatepsiTests({ query, construct }: UseSatepsiTestsParams) {
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
        useInfiniteQuery<
            SatepsiPaginatedResponse,
            Error,
            InfiniteData<SatepsiPaginatedResponse>,
            string[],
            number
        >({
            queryKey: ['satepsi-tests', query, construct ?? ''],
            queryFn: ({ pageParam }) =>
                satepsiApi.list({
                    q: query,
                    construct,
                    page: Number(pageParam) || 1,
                    pageSize: PAGE_SIZE,
                }),
            initialPageParam: 1,
            getNextPageParam: (lastPage, allPages) => {
                if (!lastPage.pagination.hasMore) return undefined;
                return allPages.length + 1;
            },
            staleTime: 1000 * 60 * 60,
        });

    const tests = useMemo<SatepsiTest[]>(() => {
        if (!data?.pages) return [];
        return data.pages.flatMap((page) => page.tests);
    }, [data]);

    const domains = useMemo(() => {
        if (!data?.pages?.[0]) return [];
        return data.pages[0].domains;
    }, [data]);

    // --- Intersection Observer for infinite scroll ---
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const sentinelCallbackRef = useCallback(
        (node: HTMLDivElement | null) => {
            // Disconnect previous observer
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            sentinelRef.current = node;

            if (!node) return;

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                        fetchNextPage();
                    }
                },
                { threshold: 0 },
            );

            observerRef.current.observe(node);
        },
        [hasNextPage, isFetchingNextPage, fetchNextPage],
    );

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return {
        tests,
        domains,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        error,
        sentinelRef: sentinelCallbackRef,
    };
}
